/**
 * ProjectLayout - Wrapper component for project-scoped views
 * 
 * This component:
 * - Extracts projectId from URL params
 * - Provides ProjectContext with the current project
 * - Renders navigation header for project views
 * - Uses React Router's Outlet for nested routes
 */

import { useEffect } from 'react'
import { useParams, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { ProjectProvider, useProject } from '../../context/ProjectContext'
import { useAuth } from '../../context/AuthContext'
import { useOnboardingContext } from '../../context/OnboardingContext'
import { useProjectEntities } from '../../hooks/useProjectEntities'
import { APP_LOGO_ICON_NAME, APP_LOGO_TEXT } from '../../config/branding'
import { ProfileDropdown } from './ProfileDropdown'
import { FeedbackButton } from './FeedbackButton'
import { Icon } from './Icon'

/**
 * Inner layout component that uses project context
 */
function ProjectLayoutInner() {
  const { user, signOut } = useAuth()
  const { project, loading, error, can } = useProject()
  const navigate = useNavigate()
  const location = useLocation()
  const { projectId } = useParams()
  const { updateEntityCounts } = useOnboardingContext()
  const { characters, events, locations, loading: entitiesLoading } = useProjectEntities(projectId)
  
  // Update onboarding context with entity counts
  useEffect(() => {
    if (project && !loading && !entitiesLoading) {
      updateEntityCounts({
        projects: 1, // User is in a project
        characters: characters?.length || 0,
        events: events?.length || 0,
        locations: locations?.length || 0,
      })
    }
  }, [project, loading, entitiesLoading, characters?.length, events?.length, locations?.length, updateEntityCounts])
  
  // Check permissions
  const canManageMembers = can('manageMembers')
  const canAccessDevPages = can('accessDevPages')
  
  // Build navigation items with project-scoped paths
  // Events is placed immediately after Timeline for quick access
  const navItems = [
    { path: '', label: 'Timeline', iconName: 'timeline' },
    { path: 'events', label: 'Events', iconName: 'events' },
    { path: 'characters', label: 'Characters', iconName: 'characters' },
    { path: 'locations', label: 'Locations', iconName: 'locations' },
    { path: 'shots', label: 'Shots', iconName: 'shots' },
    { path: 'synopsis', label: 'Synopsis', iconName: 'synopsis' },
    { path: 'images', label: 'Images', iconName: 'images' },
    { path: 'prompts', label: 'Prompts', iconName: 'prompts' },
    { path: 'import', label: 'Import', iconName: 'import' },
    { path: 'storyboard', label: 'Storyboard', iconName: 'storyboard' },
    // Members link only visible to admins
    ...(canManageMembers ? [{ path: 'members', label: 'Members', iconName: 'members' }] : []),
    // Testing and Design pages only visible to developers
    ...(canAccessDevPages ? [
      { path: 'testing', label: 'Testing', iconName: 'testing' },
      { path: 'design', label: 'Design', iconName: 'design' },
    ] : []),
    { path: 'payments', label: 'Payments', iconName: 'payments' },
  ]

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign out error:', err)
    }
  }

  const handleBackToProjects = () => {
    navigate('/projects')
  }

  // Show loading state while project is being fetched
  if (loading) {
    return (
      <div className="app">
        <div className="loading-screen">
          <div className="loading-spinner" />
          <p>Loading project...</p>
        </div>
      </div>
    )
  }

  // Show error state if project couldn't be loaded
  if (error) {
    return (
      <div className="app">
        <div className="loading-screen">
          <p style={{ color: 'var(--amber)' }}>
            <Icon name="warning" size={16} /> {error}
          </p>
          <button 
            onClick={handleBackToProjects}
            style={{ 
              marginTop: '1rem', 
              padding: '0.5rem 1rem',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: '8px',
              color: 'var(--cyan)',
              cursor: 'pointer'
            }}
          >
            Back to Projects
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="logo" onClick={handleBackToProjects} style={{ cursor: 'pointer' }}>
          <span className="logo-icon">
            <Icon name={APP_LOGO_ICON_NAME} size={24} />
          </span>
          <span className="logo-text">{APP_LOGO_TEXT}</span>
          {project && (
            <span className="project-name" style={{ 
              marginLeft: '0.75rem',
              paddingLeft: '0.75rem',
              borderLeft: '1px solid var(--glass-border)',
              color: 'var(--text-secondary)',
              fontSize: '0.875rem'
            }}>
              {project.name}
            </span>
          )}
        </div>
        <nav className="main-nav">
          {navItems.map(item => (
            <NavLink 
              key={item.path}
              to={`/projects/${projectId}/${item.path}`}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              end={item.path === ''}
              data-onboarding={item.path === '' ? 'timeline' : undefined}
            >
              <span className="nav-icon">
                <Icon name={item.iconName} size={16} />
              </span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
          {/* Back to Projects link */}
          <NavLink 
            to="/projects"
            className="nav-link"
          >
            <span className="nav-icon">
              <Icon name="projects" size={16} />
            </span>
            <span className="nav-label">Projects</span>
          </NavLink>
        </nav>
        <div className="header-actions">
          <FeedbackButton variant="header" />
          {user && (
            <ProfileDropdown user={user} onSignOut={handleSignOut} />
          )}
        </div>
      </header>
      
      <main className="app-main">
        <AnimatePresence mode="wait">
          <Outlet />
        </AnimatePresence>
      </main>
    </div>
  )
}

/**
 * ProjectLayout - Main export with ProjectProvider wrapper
 */
export default function ProjectLayout() {
  const { projectId } = useParams()
  
  return (
    <ProjectProvider initialProjectId={projectId}>
      <ProjectLayoutInner />
    </ProjectProvider>
  )
}
